import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  resetPolicyExtractorState,
  selectClient,
  selectPolicyExtractor,
  selectSession,
  selectSessionAdvisor,
} from '@client/model';
import {
  checkPolicyCalculationTypeEquals,
  defaultsRetroactiveWaitingPeriodReimbursement,
  FundingPolicyType,
  fundingPolicyTypeDefs,
  isNullOrUndefined,
  isNullUndefinedOrEmpty,
  SessionType,
} from '@shared';
import { useSearchParams } from 'react-router-dom';

export const usePolicyForm = (fundingSourceId: string | undefined, onClose: () => void) => {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const {
    mutableClientPartner,
    intakeSurvey: { clientHasStartedLtc },
  } = client;
  const policyInitialState = useInitialPolicyState(fundingSourceId);
  const [policyState, setPolicyState] = useState<PolicyFundingSource>(policyInitialState);
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<InsuranceFormErrors>({});
  const [hasChangedField, setHasChangedField] = useState(false);
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const { processedResponse, policyType } = useSelector(selectPolicyExtractor);
  const [showJointPolicyNoPartnerDialog, setShowJointPolicyNoPartnerDialog] = useState(false);
  const [maxBenefitsInterval, setMaxBenefitsInterval] = useState<MaxBenefitsIntervalType>(
    policyInitialState?.maxBenefitsInterval || 'daily',
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Ensure policyState and maxBenefitsInterval are updated when fundingSourceId changes
  useEffect(() => {
    setPolicyState(policyInitialState);
    setMaxBenefitsInterval(policyInitialState?.maxBenefitsInterval || 'daily');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingSourceId]);

  const handleChangePolicyField =
    (policyKey: PolicyFieldKey, parser?: (value: PolicyFieldValue) => PolicyFieldValue) =>
    (value: PolicyFieldValue) => {
      const parsedValue = parser ? parser(value) : value;
      setPolicyState(prevState => {
        const nextState = {
          ...prevState,
          [policyKey]: parsedValue,
        } as PolicyFundingSource;
        // When the policy type is switched to a product whose EP is paid back
        // retroactively, seed the toggle to true for users who haven't yet
        // expressed an explicit preference. Never clobber an existing value.
        if (
          policyKey === 'policyType' &&
          prevState.hasRetroactiveWaitingPeriodReimbursement == null &&
          defaultsRetroactiveWaitingPeriodReimbursement(parsedValue as FundingPolicyType | null)
        ) {
          nextState.hasRetroactiveWaitingPeriodReimbursement = true;
        }
        return nextState;
      });
      setHasChangedField(true);
    };

  const handleIntervalChange = (interval: MaxBenefitsIntervalType) => {
    setMaxBenefitsInterval(interval);
    setHasChangedField(true);
  };

  const resetState = () => {
    setPolicyState(policyInitialState);
    setFormHasBeenSubmitted(false);
    setFormErrors({});
    setHasChangedField(false);
    setPolicyFile(null);
    setMaxBenefitsInterval(policyInitialState?.maxBenefitsInterval || 'daily');
    dispatch(resetPolicyExtractorState());
  };

  const validateForm = (newPolicyState?: PolicyFundingSource) => {
    const policyStateToValidate = newPolicyState || policyState;

    let formIsValid = true;
    const newFormErrors = {} as InsuranceFormErrors;

    if (!policyStateToValidate.policyType) {
      newFormErrors.policyType = true;
      formIsValid = false;
    }

    const policyTypeDef = policyStateToValidate.policyType
      ? fundingPolicyTypeDefs[policyStateToValidate.policyType]
      : undefined;
    const isPolicyJoint = policyTypeDef?.isJoint;

    const isPolicyTypeAnnuityHybrid = checkPolicyCalculationTypeEquals(
      policyStateToValidate.policyType,
      FundingPolicyType.annuityHybrid,
    );
    const isPolicyTypeHybridIRA = checkPolicyCalculationTypeEquals(
      policyStateToValidate.policyType,
      FundingPolicyType.hybridLifeIra,
    );

    if (isPolicyJoint && !mutableClientPartner) {
      newFormErrors.jointPolicyNoPartner = true;
      formIsValid = false;
      setShowJointPolicyNoPartnerDialog(true);
    }

    if (isNullUndefinedOrEmpty(policyStateToValidate.policyPremiumStartYear)) {
      newFormErrors.policyPremiumStartYear = true;
      formIsValid = false;
    }

    // require policy monthly withdrawal rate for hybrid life IRA policies
    if (
      isPolicyTypeHybridIRA &&
      isNullUndefinedOrEmpty(policyStateToValidate.policyMonthlyWithdrawalRate)
    ) {
      newFormErrors.policyMonthlyWithdrawalRate = true;
      formIsValid = false;
    }

    if (
      isPolicyTypeHybridIRA &&
      isNullUndefinedOrEmpty(policyStateToValidate.policyMaximumBenefitAmount)
    ) {
      newFormErrors.policyMaximumBenefitAmount = true;
      formIsValid = false;
    }

    // on-claim specific checks
    if (clientHasStartedLtc) {
      if (
        isNullUndefinedOrEmpty(
          policyStateToValidate.policyBenefitUtilizedToDate,
        )
      ) {
        newFormErrors.policyBenefitUtilizedToDate = true;
        formIsValid = false;
      }
      if (
        policyStateToValidate.policyBenefitUtilizedToDate &&
        policyStateToValidate.policyBenefitUtilizedToDate > 0 &&
        policyStateToValidate.homeCareWaitingPeriodDays &&
        policyStateToValidate.homeCareWaitingPeriodDays > 0 &&
        policyStateToValidate.facilityCareWaitingPeriodDays &&
        policyStateToValidate.facilityCareWaitingPeriodDays > 0
      ) {
        newFormErrors.waitingPeriodAndBenefitsUtilized = true;
        formIsValid = false;
      }
    }

    // require policy benefit period months for short term care insurance policies
    if (
      checkPolicyCalculationTypeEquals(
        policyStateToValidate.policyType,
        FundingPolicyType.shortTermCareInsurance,
      )
    ) {
      if (
        isNullUndefinedOrEmpty(policyStateToValidate.policyBenefitPeriodMonths)
      ) {
        newFormErrors.policyBenefitPeriodMonths = true;
        formIsValid = false;
      }
    }

    // require premium monthly cost for pre-claim users with non-annuity hybrid policies
    if (
      !clientHasStartedLtc && //pre-claim
      !isPolicyTypeAnnuityHybrid // not hybrid and pre-claim
    ) {
      if (
        isNullUndefinedOrEmpty(policyStateToValidate.policyPremiumMonthlyCost)
      ) {
        newFormErrors.policyPremiumMonthlyCost = true;
        formIsValid = false;
      }
    }

    // require lump sum payment for annuity hybrid policies
    if (isPolicyTypeAnnuityHybrid) {
      if (isNullUndefinedOrEmpty(policyStateToValidate.policyLumpSumPayment)) {
        newFormErrors.policyLumpSumPayment = true;
        formIsValid = false;
      }
    }

    if (policyStateToValidate.isIndemnityPolicyPayment) {
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyAssistedLivingBenefitAmount)) {
        newFormErrors.dailyAssistedLivingBenefitAmount = true;
        formIsValid = false;
      }
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyHomeBenefitAmount)) {
        newFormErrors.dailyHomeBenefitAmount = true;
        formIsValid = false;
      }
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyIndependentLivingBenefitAmount)) {
        newFormErrors.dailyIndependentLivingBenefitAmount = true;
        formIsValid = false;
      }
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyNursingHomeBenefitAmount)) {
        newFormErrors.dailyNursingHomeBenefitAmount = true;
        formIsValid = false;
      }
    }

    setFormErrors(newFormErrors);
    return formIsValid;
  };

  const onSubmit = () => {
    setFormHasBeenSubmitted(true);
    if (!validateForm()) {
      return;
    }

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: {
          ...policyState,
          maxBenefitsInterval,
          policyAddedAt: policyState?.policyAddedAt || Date.now(),
        },
        fundingSourceId: fundingSourceId ?? null,
      }),
    );

    const newParams = new URLSearchParams(searchParams);
    newParams.set('downloadReport', 'true');
    setSearchParams(newParams, { replace: true });

    onClose();
    resetState();
  };

  useEffect(() => {
    // revalidate form when form has been submitted or there are form errors
    if (formHasBeenSubmitted || Object.keys(formErrors).length > 0) {
      validateForm();
    }
  }, [policyState]);

  const onImportPolicyFieldValues = () => {
    if (!processedResponse) {
      return;
    }
    const newPolicyState = collectPolicyStateFromExtractedData(processedResponse);
    setPolicyState(newPolicyState);
    setHasChangedField(true);

    if (validateForm(newPolicyState)) {
      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: {
            ...newPolicyState,
            policyAddedAt: Date.now(),
          },
          fundingSourceId: null,
        }),
      );
      onClose();
      resetState();
    }
  };

  const collectPolicyStateFromExtractedData = (
    processedResponse: ParsedPolicyDataExtractResponse,
  ) => {
    const extractedData = {
      policyType: policyType,
    } as Partial<PolicyFundingSource>;

    const divideByHundred = (value: number) => value / 100;

    const setFieldValue = (
      parsedField: (PolicyExtractedField & { parsedFieldValue: any }) | undefined,
      policyFieldKey: PolicyFieldKey,
      transformValue?: (value: any) => any,
    ) => {
      if (parsedField && !isNullOrUndefined(parsedField.parsedFieldValue)) {
        extractedData[policyFieldKey] = transformValue
          ? transformValue(parsedField.parsedFieldValue)
          : parsedField.parsedFieldValue;
      }
    };

    // Add all field mappings
    setFieldValue(
      processedResponse.policyMinimumGuaranteedBenefitAmount,
      'policyMinimumGuaranteedBenefitAmount',
    );
    setFieldValue(processedResponse.policyLumpSumPayment, 'policyLumpSumPayment');
    setFieldValue(
      processedResponse.policyInflationProtection,
      'policyInflationProtection',
      divideByHundred,
    );
    setFieldValue(processedResponse.policyPremiumMonthlyCost, 'policyPremiumMonthlyCost');
    setFieldValue(processedResponse.policyPremiumStartYear, 'policyPremiumStartYear');

    setFieldValue(processedResponse.policyMaximumBenefitAmount, 'policyMaximumBenefitAmount');
    setFieldValue(
      processedResponse.dailyAssistedLivingBenefitAmount,
      'dailyAssistedLivingBenefitAmount',
    );

    setFieldValue(processedResponse.dailyHomeBenefitAmount, 'dailyHomeBenefitAmount');

    setFieldValue(
      processedResponse.dailyIndependentLivingBenefitAmount,
      'dailyIndependentLivingBenefitAmount',
    );

    setFieldValue(processedResponse.dailyNursingHomeBenefitAmount, 'dailyNursingHomeBenefitAmount');
    setFieldValue(processedResponse.homeCareWaitingPeriodDays, 'homeCareWaitingPeriodDays');
    setFieldValue(processedResponse.facilityCareWaitingPeriodDays, 'facilityCareWaitingPeriodDays');
    setFieldValue(processedResponse.policyLimitedPayYears, 'policyLimitedPayYears');
    setFieldValue(processedResponse.policyBenefitPeriodMonths, 'policyBenefitPeriodMonths');
    setFieldValue(
      processedResponse.policyContinuationBenefitAmount,
      'policyContinuationBenefitAmount',
    );
    setFieldValue(
      processedResponse.policyMonthlyWithdrawalRate,
      'policyMonthlyWithdrawalRate',
      divideByHundred,
    );
    setFieldValue(
      processedResponse.policyPremiumIncreaseRate,
      'policyPremiumIncreaseRate',
      divideByHundred,
    );
    setFieldValue(
      processedResponse.policyInflationProtectionOnFaceAmount,
      'policyInflationProtectionOnFaceAmount',
      divideByHundred,
    );
    setFieldValue(
      processedResponse.policyInflationProtectionOnCOBAmount,
      'policyInflationProtectionOnCOBAmount',
      divideByHundred,
    );
    setFieldValue(
      processedResponse.policyInflationProtectionYears,
      'policyInflationProtectionYears',
    );

    // Handle special cases
    if (processedResponse.isPrimaryPolicyHolder?.fieldValue) {
      extractedData.isPrimaryPolicyHolder =
        processedResponse.isPrimaryPolicyHolder.fieldValue.toLowerCase() === 'yes';
    }

    if (processedResponse.policyPaymentType?.fieldValue) {
      extractedData.isIndemnityPolicyPayment =
        processedResponse.policyPaymentType.fieldValue.toLowerCase() === 'indemnity';
    }

    if (processedResponse.policyInflationProtectionType?.fieldValue) {
      extractedData.isSimpleInflationProtection =
        processedResponse.policyInflationProtectionType.fieldValue.toLowerCase() === 'simple';
    }

    // Update state all at once
    const newPolicyState = {
      ...policyState,
      ...extractedData,
    } as PolicyFundingSource;
    return newPolicyState;
  };

  const handleFileUpload = (file: File | null) => {
    setPolicyFile(file);
    setHasChangedField(true);
  };

  return {
    policyState,
    formErrors,
    hasChangedField,
    onSubmit,
    resetState,
    handleChangePolicyField,
    onImportPolicyFieldValues,
    showJointPolicyNoPartnerDialog,
    setShowJointPolicyNoPartnerDialog,
    policyFile,
    setPolicyFile: handleFileUpload,
    maxBenefitsInterval,
    handleMaxBenefitsIntervalChange: handleIntervalChange,
  };
};

export interface InsuranceFormErrors extends ObjectMap<boolean | undefined> {
  policyInflationProtection?: boolean;
  jointPolicyNoPartner?: boolean;
}

const emptyPolicyState = (
  isAdvisorSession: boolean,
  advisorId: string | undefined,
): PolicyFundingSource =>
  ({
    policyType: null,
    hasPolicyFunding: true,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
  }) as PolicyFundingSource;

const useInitialPolicyState = (fundingSourceId?: string) => {
  const { sessionType } = useSelector(selectSession);
  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const advisor = useSelector(selectSessionAdvisor);
  const { multipleFundingSources } = useSelector(selectClient);
  if (fundingSourceId && multipleFundingSources) {
    const existing = multipleFundingSources.ltcPolicy[fundingSourceId];
    if (existing) {
      return existing;
    }
  }
  return emptyPolicyState(isAdvisorSession, advisor?.advisorId);
};
